import useCarData from '@/features/cars/hooks/useCarData';
import { ExtendedCarItem } from '@/features/cars/types';
import filterHiddenBlocks from '@/utils/filterHiddenBlocks';

export type ConstructorItem = {
  id: string | number;
  skipRenderFn?: (carItem?: ExtendedCarItem) => boolean;
  items?: Array<ConstructorItem>;
};

export interface ConstructorConfig {
  items: Array<ConstructorItem>;
}

/**
 * Фильтрует блоки конструктора, которые необходимо скрыть.
 * @param constructor конструктор
 *
 * @returns Возвращает новый объект конструктора без отфильтрованных блоков.
 * Если для фильтрации блоков необходим параметр автомобиля, но данные по нему еще не получены,
 * функция возвращает тот же объект constructor, до тех пор, пока данные по автомобилю не будут получены.
 * Это необходимо для корректного отображения скелетона.
 * Алгоритм фильтрации см @see filterHiddenBlocks()
 */
function useFilterConstructor<T extends ConstructorConfig>(
  constructor: T | undefined,
): T | undefined {
  const carData = useCarData();

  // carData.isUninitialized === false означает, что в контексте, где используется
  // переданный конструктор нет объекта автомобиля.
  // Если true, то данные по автомобилю присутствуют, однако сами данные могут еще быть не получены с сервера.
  const isCarPresent = !carData.isUninitialized;

  if (constructor === undefined) {
    return undefined;
  }

  // Если в текущем контексте нет объекта автомобиля, фильтрация выполняется без него.
  if (!isCarPresent) {
    return filterHiddenBlocks(constructor);
  }

  // Если объект автомобиля получен, используем его как параметр для фильтрации блоков.
  if (isCarPresent && carData.data) {
    return filterHiddenBlocks(constructor, carData.data);
  }

  // Если запрос на объект автомобиля еще выполняется, возвращаем
  // текущий конструктор, для корректного отображения скелетона
  return constructor;
}

export default useFilterConstructor;
